import { getApiBaseUrl, readApiErrorMessage } from '../utils/apiClient';
import { getAdminKey } from './adminKeyStorage';

export type WorkflowStatus =
  | 'queued'
  | 'needs_review'
  | 'in_review'
  | 'approved'
  | 'approved_with_note'
  | 'held'
  | 'needs_retry'
  | 'retry_in_progress'
  | 'delivered'
  | 'feedback_requested'
  | 'feedback_received'
  | 'archived';

export type DeliveryStatus =
  | 'not_ready'
  | 'ready_for_review'
  | 'approved_internal'
  | 'delivered'
  | 'held'
  | 'failed'
  | 'retry_required';

export type AutoGateStatus =
  | 'not_evaluated'
  | 'auto_approved'
  | 'needs_review'
  | 'held'
  | 'retry_required'
  | 'failed';

export type ManualReviewStatus =
  | 'pending'
  | 'pass'
  | 'pass_with_note'
  | 'hold'
  | 'fail'
  | 'not_applicable';

export type ScanStatus = 'not_run' | 'pass' | 'fail';

export type ReviewDecision =
  | 'approve'
  | 'approve_with_note'
  | 'hold'
  | 'retry_required'
  | 'fail';

export interface ReviewItem {
  review_item_id: string;
  upload_id: string;
  report_id: string;
  created_at?: string;
  updated_at?: string;
  workflow_status?: WorkflowStatus;
  manual_review_status?: ManualReviewStatus;
  delivery_status?: DeliveryStatus;
  auto_gate_status?: AutoGateStatus;
  auto_gate_score?: number;
  auto_gate_reasons?: string[];
  risk_flags?: string[];
  hold_reason?: string | null;
  failure_class?: string | null;
  report_type?: string;
  league?: string | null;
  owner_team?: string | null;
  opponent_team?: string | null;
  opponent_gamertag?: string | null;
  analysis_quality?: string | null;
  projection_scan_status?: ScanStatus;
  projection_scan_hits_count?: number;
  forbidden_scan_status?: ScanStatus;
  forbidden_scan_hits_count?: number;
  projection_scan_hits?: unknown[];
  forbidden_scan_hits?: unknown[];
  reviewer?: string | null;
  review_started_at?: string | null;
  review_completed_at?: string | null;
  review_notes_internal?: string | null;
  customer_safe_summary?: string | null;
  delivery_recipient?: string | null;
  projected_payload_url?: string;
  full_results_uri_internal?: string | null;
  analysis_diagnostics_uri_internal?: string | null;
  delivered_at?: string | null;
  delivered_by?: string | null;
  feedback_requested_at?: string | null;
  source?: Record<string, unknown>;
  version?: number;
}

export interface ReviewListParams {
  workflow_status?: string;
  delivery_status?: string;
  auto_gate_status?: string;
  since?: string;
  q?: string;
  limit?: number;
  cursor?: string;
  include_internal?: boolean;
}

export interface ReviewListResponse {
  items: ReviewItem[];
  next_cursor?: string | null;
}

export interface DiscoverPayload {
  dry_run?: boolean;
  create_missing?: boolean;
  limit?: number;
  since?: string;
  upload_id?: string;
}

export interface DiscoverResponse {
  dry_run: boolean;
  would_create_count: number;
  created_count: number;
  would_create?: Array<{ upload_id: string; item: ReviewItem }>;
  created?: string[];
}

export interface ScanPayload {
  run_projection?: boolean;
  run_forbidden_copy?: boolean;
}

export interface PatchPayload {
  league?: string;
  report_type?: string;
  owner_team?: string;
  opponent_team?: string;
  opponent_gamertag?: string;
  delivery_recipient?: string;
  customer_safe_summary?: string;
  review_notes_internal?: string;
  hold_reason?: string | null;
  failure_class?: string | null;
}

export interface DecisionPayload {
  decision: ReviewDecision;
  reviewer: string;
  review_notes_internal?: string;
  customer_safe_summary?: string;
  hold_reason?: string | null;
  checklist?: Record<string, unknown>;
}

export interface MarkDeliveredPayload {
  delivered_by?: string;
  customer_safe_summary?: string;
}

export interface MotionClaimPromotionContract {
  promotion_status: string;
  requires_human_approval: boolean;
  customer_projection_candidate: boolean;
  customer_visible: boolean;
  normalized: {
    play_type: string;
    concept_family: string;
    concept_display: string;
    attack_area: string;
    attack_area_display: string;
  };
  safe_wording: {
    admin_review: string;
    future_customer_candidate: string | null;
  };
  normalization_warnings: string[];
  future_promotion_eligible?: boolean;
}

export interface MotionClaimApprovalOverlay {
  approval_status: string;
  reviewer_id?: string;
  reviewer_type?: string;
  reviewed_at?: string;
  reviewer_note?: string | null;
  approved_wording?: string | null;
  customer_projection_candidate: boolean;
  customer_visible: boolean;
}

export interface MotionClaimReviewClaim {
  window_id: string;
  claim_gate_status: 'customer_safe' | 'coach_review_only' | string;
  play_type: string;
  concept_family?: string | null;
  attack_area?: string | null;
  confidence_label: string;
  evidence_label: string;
  review_wording: string;
  source: string;
  promotion_contract?: MotionClaimPromotionContract;
  approval?: MotionClaimApprovalOverlay;
}

export interface MotionClaimReviewResponse {
  upload_id: string;
  review_mode: string;
  customer_visible: boolean;
  claims: MotionClaimReviewClaim[];
  summary: {
    customer_safe_count: number;
    coach_review_only_count: number;
    shadow_only_count: number;
    blocked_count: number;
  };
  promotion_contract?: {
    promotion_contract_version: string;
    customer_visible: boolean;
    customer_projection_candidate: boolean;
    requires_human_approval: boolean;
    status_counts: Record<string, number>;
    future_promotion_eligible_count: number;
  };
  motion_claim_approval_summary?: {
    approved_count: number;
    rejected_count: number;
    needs_more_review_count: number;
    internal_only_count: number;
    last_reviewed_at?: string | null;
  };
}

export interface EvaluateResponse {
  decision: Record<string, unknown>;
  item: ReviewItem;
}

export interface ScanResponse {
  projection_scan?: { status?: ScanStatus; hits_count?: number };
  forbidden_scan?: { status?: ScanStatus; hits_count?: number };
  item: ReviewItem;
}

export class AdminReviewApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'AdminReviewApiError';
    this.status = status;
  }
}

async function adminFetch(path: string, init?: RequestInit): Promise<Response> {
  const key = getAdminKey();
  if (!key) {
    throw new AdminReviewApiError('Admin key missing or invalid', 401);
  }

  const headers = new Headers(init?.headers);
  headers.set('X-Admin-Key', key);
  if (init?.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return fetch(`${getApiBaseUrl()}${normalizedPath}`, {
    ...init,
    headers,
    credentials: 'omit',
  });
}

async function parseAdminJson<T>(response: Response): Promise<T> {
  if (response.ok) {
    return response.json() as Promise<T>;
  }

  const message =
    (await readApiErrorMessage(response)) || `Request failed (${response.status})`;

  if (response.status === 401 || response.status === 403) {
    throw new AdminReviewApiError('Admin key missing or invalid', response.status);
  }

  throw new AdminReviewApiError(message, response.status);
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === '') return;
    search.set(key, String(value));
  });
  const query = search.toString();
  return query ? `?${query}` : '';
}

export async function listReviewItems(params: ReviewListParams = {}): Promise<ReviewListResponse> {
  const query = buildQuery({
    workflow_status: params.workflow_status,
    delivery_status: params.delivery_status,
    auto_gate_status: params.auto_gate_status,
    since: params.since,
    q: params.q,
    limit: params.limit ?? 50,
    cursor: params.cursor,
    include_internal: params.include_internal ? 'true' : undefined,
  });
  const response = await adminFetch(`/api/admin/review-items${query}`);
  return parseAdminJson<ReviewListResponse>(response);
}

export async function getReviewItem(
  uploadId: string,
  includeInternal = true
): Promise<ReviewItem> {
  const query = includeInternal ? '?include_internal=true' : '';
  const response = await adminFetch(`/api/admin/review-items/${encodeURIComponent(uploadId)}${query}`);
  return parseAdminJson<ReviewItem>(response);
}

export async function discoverReviewItems(payload: DiscoverPayload): Promise<DiscoverResponse> {
  const response = await adminFetch('/api/admin/review-items/discover', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return parseAdminJson<DiscoverResponse>(response);
}

export async function evaluateReviewItem(uploadId: string): Promise<EvaluateResponse> {
  const response = await adminFetch(
    `/api/admin/review-items/${encodeURIComponent(uploadId)}/evaluate`,
    { method: 'POST' }
  );
  return parseAdminJson<EvaluateResponse>(response);
}

export async function scanReviewItem(
  uploadId: string,
  payload: ScanPayload = {}
): Promise<ScanResponse> {
  const response = await adminFetch(
    `/api/admin/review-items/${encodeURIComponent(uploadId)}/scan`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
  return parseAdminJson<ScanResponse>(response);
}

export async function patchReviewItem(
  uploadId: string,
  payload: PatchPayload
): Promise<ReviewItem> {
  const response = await adminFetch(
    `/api/admin/review-items/${encodeURIComponent(uploadId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }
  );
  return parseAdminJson<ReviewItem>(response);
}

export async function claimReviewItem(uploadId: string, reviewer: string): Promise<ReviewItem> {
  const response = await adminFetch(
    `/api/admin/review-items/${encodeURIComponent(uploadId)}/claim`,
    {
      method: 'POST',
      body: JSON.stringify({ reviewer }),
    }
  );
  return parseAdminJson<ReviewItem>(response);
}

export async function decideReviewItem(
  uploadId: string,
  payload: DecisionPayload
): Promise<ReviewItem> {
  const response = await adminFetch(
    `/api/admin/review-items/${encodeURIComponent(uploadId)}/decision`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
  return parseAdminJson<ReviewItem>(response);
}

export async function markDelivered(
  uploadId: string,
  payload: MarkDeliveredPayload = {}
): Promise<ReviewItem> {
  const response = await adminFetch(
    `/api/admin/review-items/${encodeURIComponent(uploadId)}/mark-delivered`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
  return parseAdminJson<ReviewItem>(response);
}

export async function requestFeedback(uploadId: string): Promise<ReviewItem> {
  const response = await adminFetch(
    `/api/admin/review-items/${encodeURIComponent(uploadId)}/request-feedback`,
    { method: 'POST', body: JSON.stringify({}) }
  );
  return parseAdminJson<ReviewItem>(response);
}

export async function fetchMotionClaimReview(
  uploadId: string
): Promise<MotionClaimReviewResponse> {
  const response = await adminFetch(
    `/api/admin/reports/${encodeURIComponent(uploadId)}/motion-claim-review`
  );
  return parseAdminJson<MotionClaimReviewResponse>(response);
}

export interface MotionProjectionDryRunStatement {
  window_id: string;
  source: string;
  concept_family: string;
  concept_display: string;
  play_type: string;
  attack_area: string;
  attack_area_display: string;
  statement: string;
  confidence_label: string;
  evidence_label: string;
  approval_status: string;
  reviewer_id?: string;
  customer_visible: boolean;
  publish_ready: boolean;
}

export interface MotionProjectionExcludedClaim {
  window_id: string;
  reason: string;
  warnings: string[];
}

export interface MotionProjectionDryRunResponse {
  upload_id: string;
  projection_mode: string;
  projection_contract_version?: string;
  customer_visible: boolean;
  publish_ready: boolean;
  generated_at: string;
  motion_projection_summary: {
    eligible_claim_count: number;
    projected_statement_count: number;
    excluded_claim_count: number;
  };
  projected_motion_statements: MotionProjectionDryRunStatement[];
  excluded_claims: MotionProjectionExcludedClaim[];
}

export async function fetchMotionClaimProjectionDryRun(
  uploadId: string
): Promise<MotionProjectionDryRunResponse> {
  const response = await adminFetch(
    `/api/admin/reports/${encodeURIComponent(uploadId)}/motion-claim-projection-dry-run`
  );
  return parseAdminJson<MotionProjectionDryRunResponse>(response);
}

/** @deprecated Use fetchMotionClaimProjectionDryRun — admin key comes from session storage. */
export const getMotionClaimProjectionDryRun = fetchMotionClaimProjectionDryRun;

export interface MotionPublicationCandidate {
  upload_id: string;
  window_id: string;
  source: string;
  approval_status: string;
  publication_status: string;
  statement: string;
  concept_family: string;
  concept_display: string;
  play_type?: string;
  attack_area: string;
  attack_area_display: string;
  confidence_label?: string;
  evidence_label?: string;
  reviewer_id?: string;
  publisher_id?: string;
  publisher_note?: string | null;
  published_at?: string;
  dry_run_generated_at?: string;
  revoked_at?: string | null;
  revoke_reason?: string | null;
  customer_visible: boolean;
  customer_projection_candidate: boolean;
  publish_ready: boolean;
  published_to_report: boolean;
  published_to_evidence_reel: boolean;
  delivered: boolean;
}

export interface MotionPublicationCandidatesResponse {
  upload_id: string;
  candidates: MotionPublicationCandidate[];
  summary: {
    active_count: number;
    revoked_count: number;
    last_published_at?: string | null;
  };
}

export interface CreatePublicationCandidateRequest {
  publisher_id: string;
  publisher_note?: string;
}

export interface RevokePublicationCandidateRequest {
  publisher_id: string;
  revoke_reason?: string;
}

export interface PublicationCandidateMutationResponse {
  success: boolean;
  upload_id: string;
  window_id: string;
  candidate: MotionPublicationCandidate;
}

export async function fetchMotionClaimPublicationCandidates(
  uploadId: string
): Promise<MotionPublicationCandidatesResponse> {
  const response = await adminFetch(
    `/api/admin/reports/${encodeURIComponent(uploadId)}/motion-claim-publication-candidates`
  );
  return parseAdminJson<MotionPublicationCandidatesResponse>(response);
}

export async function createMotionClaimPublicationCandidate(
  uploadId: string,
  windowId: string,
  payload: CreatePublicationCandidateRequest
): Promise<PublicationCandidateMutationResponse> {
  const response = await adminFetch(
    `/api/admin/reports/${encodeURIComponent(uploadId)}/motion-claims/${encodeURIComponent(windowId)}/publication-candidate`,
    {
      method: 'POST',
      body: JSON.stringify({
        ...payload,
        publisher_id: normalizeReviewerId(payload.publisher_id),
      }),
    }
  );
  return parseAdminJson<PublicationCandidateMutationResponse>(response);
}

export async function revokeMotionClaimPublicationCandidate(
  uploadId: string,
  windowId: string,
  payload: RevokePublicationCandidateRequest
): Promise<PublicationCandidateMutationResponse> {
  const response = await adminFetch(
    `/api/admin/reports/${encodeURIComponent(uploadId)}/motion-claims/${encodeURIComponent(windowId)}/publication-candidate`,
    {
      method: 'DELETE',
      body: JSON.stringify({
        ...payload,
        publisher_id: normalizeReviewerId(payload.publisher_id),
      }),
    }
  );
  return parseAdminJson<PublicationCandidateMutationResponse>(response);
}

export interface MotionReportInsertionCandidate {
  upload_id: string;
  window_id: string;
  source: string;
  publication_status: string;
  insertion_status: string;
  statement: string;
  section: string;
  section_title: string;
  concept_family: string;
  concept_display: string;
  attack_area: string;
  attack_area_display: string;
  publisher_id?: string;
  inserted_by?: string;
  inserted_at?: string;
  insertion_note?: string | null;
  revoked_at?: string | null;
  revoke_reason?: string | null;
  customer_visible: boolean;
  customer_projection_candidate: boolean;
  publish_ready: boolean;
  published_to_report: boolean;
  visible_in_motion_review_notes?: boolean;
  published_to_evidence_reel: boolean;
  delivered: boolean;
}

export interface MotionReportInsertionCandidatesResponse {
  upload_id: string;
  candidates: MotionReportInsertionCandidate[];
  summary: {
    active_count: number;
    revoked_count: number;
    last_inserted_at?: string | null;
  };
}

export interface CreateReportInsertionCandidateRequest {
  inserted_by: string;
  insertion_note?: string;
}

export interface RevokeReportInsertionCandidateRequest {
  inserted_by: string;
  revoke_reason?: string;
}

export interface ReportInsertionCandidateMutationResponse {
  success: boolean;
  upload_id: string;
  window_id: string;
  candidate: MotionReportInsertionCandidate;
}

export async function fetchMotionClaimReportInsertionCandidates(
  uploadId: string
): Promise<MotionReportInsertionCandidatesResponse> {
  const response = await adminFetch(
    `/api/admin/reports/${encodeURIComponent(uploadId)}/motion-claim-report-insertion-candidates`
  );
  return parseAdminJson<MotionReportInsertionCandidatesResponse>(response);
}

export async function createMotionClaimReportInsertionCandidate(
  uploadId: string,
  windowId: string,
  payload: CreateReportInsertionCandidateRequest
): Promise<ReportInsertionCandidateMutationResponse> {
  const response = await adminFetch(
    `/api/admin/reports/${encodeURIComponent(uploadId)}/motion-claims/${encodeURIComponent(windowId)}/report-insertion-candidate`,
    {
      method: 'POST',
      body: JSON.stringify({
        ...payload,
        inserted_by: normalizeReviewerId(payload.inserted_by),
      }),
    }
  );
  return parseAdminJson<ReportInsertionCandidateMutationResponse>(response);
}

export async function revokeMotionClaimReportInsertionCandidate(
  uploadId: string,
  windowId: string,
  payload: RevokeReportInsertionCandidateRequest
): Promise<ReportInsertionCandidateMutationResponse> {
  const response = await adminFetch(
    `/api/admin/reports/${encodeURIComponent(uploadId)}/motion-claims/${encodeURIComponent(windowId)}/report-insertion-candidate`,
    {
      method: 'DELETE',
      body: JSON.stringify({
        ...payload,
        inserted_by: normalizeReviewerId(payload.inserted_by),
      }),
    }
  );
  return parseAdminJson<ReportInsertionCandidateMutationResponse>(response);
}

export interface MotionReviewNotesPreviewItem {
  window_id: string;
  statement: string;
  concept_family: string;
  concept_display: string;
  attack_area: string;
  attack_area_display: string;
  customer_visible: boolean;
  published_to_report: boolean;
}

export interface MotionReviewNotesPreviewResponse {
  upload_id: string;
  projection_mode: string;
  customer_visible: boolean;
  published_to_report: boolean;
  generated_at: string;
  section: {
    section_key: string;
    title: string;
    disclaimer: string;
    items: MotionReviewNotesPreviewItem[];
  };
  summary: {
    item_count: number;
    excluded_count: number;
  };
  excluded: Array<{ window_id: string; reason: string }>;
}

export async function fetchMotionReviewNotesPreview(
  uploadId: string
): Promise<MotionReviewNotesPreviewResponse> {
  const response = await adminFetch(
    `/api/admin/reports/${encodeURIComponent(uploadId)}/motion-review-notes-preview`
  );
  return parseAdminJson<MotionReviewNotesPreviewResponse>(response);
}

export interface PublishMotionReviewNoteToReportRequest {
  inserted_by: string;
  publish_note?: string;
}

export interface RevokeMotionReviewNoteFromReportRequest {
  inserted_by: string;
  revoke_reason?: string;
}

export interface CustomerReportInsertedMutationResponse {
  success: boolean;
  upload_id: string;
  window_id: string;
  candidate: MotionReportInsertionCandidate;
}

export async function publishMotionReviewNoteToReport(
  uploadId: string,
  windowId: string,
  payload: PublishMotionReviewNoteToReportRequest
): Promise<CustomerReportInsertedMutationResponse> {
  const response = await adminFetch(
    `/api/admin/reports/${encodeURIComponent(uploadId)}/motion-claims/${encodeURIComponent(windowId)}/customer-report-inserted`,
    {
      method: 'POST',
      body: JSON.stringify({
        ...payload,
        inserted_by: normalizeReviewerId(payload.inserted_by),
      }),
    }
  );
  return parseAdminJson<CustomerReportInsertedMutationResponse>(response);
}

export async function revokeMotionReviewNoteFromReport(
  uploadId: string,
  windowId: string,
  payload: RevokeMotionReviewNoteFromReportRequest
): Promise<CustomerReportInsertedMutationResponse> {
  const response = await adminFetch(
    `/api/admin/reports/${encodeURIComponent(uploadId)}/motion-claims/${encodeURIComponent(windowId)}/customer-report-inserted`,
    {
      method: 'DELETE',
      body: JSON.stringify({
        ...payload,
        inserted_by: normalizeReviewerId(payload.inserted_by),
      }),
    }
  );
  return parseAdminJson<CustomerReportInsertedMutationResponse>(response);
}

export interface MotionReviewNotesInsertionRecord {
  window_id: string;
  statement: string;
  concept_display: string;
  concept_family: string;
  attack_area_display: string;
  attack_area: string;
  insertion_status: string;
  visible_in_motion_review_notes: boolean;
  published_to_report: boolean;
  customer_visible: boolean;
  published_to_evidence_reel: boolean;
  delivered: boolean;
  inserted_by?: string | null;
  inserted_at?: string | null;
  customer_report_inserted_by?: string | null;
  customer_report_inserted_at?: string | null;
  revoked_at?: string | null;
  revoke_reason?: string | null;
}

export interface MotionReviewNotesInsertionRecordsResponse {
  success: boolean;
  upload_id: string;
  summary: {
    total_count: number;
    candidate_count: number;
    inserted_count: number;
    revoked_count: number;
    rejected_count: number;
    needs_review_count: number;
    unknown_count: number;
  };
  records: MotionReviewNotesInsertionRecord[];
}

export async function fetchMotionReviewNotesInsertionRecords(
  uploadId: string
): Promise<MotionReviewNotesInsertionRecordsResponse> {
  const response = await adminFetch(
    `/api/admin/reports/${encodeURIComponent(uploadId)}/motion-claim-report-insertion-records`
  );
  return parseAdminJson<MotionReviewNotesInsertionRecordsResponse>(response);
}

export type MotionClaimApprovalAction =
  | 'approve_for_future_projection'
  | 'reject_for_customer_projection'
  | 'needs_more_review'
  | 'mark_internal_only';

/** Normalize admin reviewer ID before submit (trim, fix colon spacing). */
export function normalizeReviewerId(value: string): string {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return '';
  }
  if (trimmed.includes(':')) {
    const colonIndex = trimmed.indexOf(':');
    const prefix = trimmed.slice(0, colonIndex).trim();
    const suffix = trimmed.slice(colonIndex + 1).trim();
    return `${prefix}:${suffix}`;
  }
  return trimmed.split(/\s+/).join(' ');
}

export interface MotionClaimApprovalRequest {
  action: MotionClaimApprovalAction;
  reviewer_id: string;
  reviewer_note?: string;
  approved_wording?: string;
}

export interface MotionClaimApprovalResponse {
  success: boolean;
  upload_id: string;
  window_id: string;
  approval: MotionClaimApprovalOverlay;
}

export async function submitMotionClaimApproval(
  uploadId: string,
  windowId: string,
  payload: MotionClaimApprovalRequest
): Promise<MotionClaimApprovalResponse> {
  const response = await adminFetch(
    `/api/admin/reports/${encodeURIComponent(uploadId)}/motion-claims/${encodeURIComponent(windowId)}/approval`,
    {
      method: 'POST',
      body: JSON.stringify({
        ...payload,
        reviewer_id: normalizeReviewerId(payload.reviewer_id),
      }),
    }
  );
  return parseAdminJson<MotionClaimApprovalResponse>(response);
}
